// @rpg-unit/sfx-kits: sound cues for @rpg/audio, in kits (tier 3; composition framework, decision 0077). 28 cues cut
// from spike 002's `content/tables/sounds.json` (phase D2, decisions 0045 and 0063), every one made in code (a recipe in
// the audio package's recipe language, no files), each played with seeded variants and a little jitter:
//   - ui: a tick on a press, a softer move between items, a back;
//   - footsteps: by surface (grass, dirt, stone, sand, water), played on a foot's plant;
//   - pickups: a pickup, gathering a herb or ore, the level-up chime;
//   - combat: hits by kind (light, heavy, skill, big, chip, parry, guard break), a defeat, swings by weapon (sword,
//     staff, wand), the dodge and the block;
//   - creatures: an enemy's growl as it turns alert;
//   - props: a crate breaking, a pot shattering.
// Important cues (a big hit, a parry, a guard break, a defeat, a growl, a break, the level-up) send a caption every time
// they play: each names a caption string (`caption.*`), which the game's strings table carries; `SFX_KIT_STRINGS` holds
// English defaults.
//
// A game takes them as content (`include.json`, or `SFX_KIT_CONTENT` in the browser: the `sfx` table), as @rpg/audio's
// `SoundDef`s (`toSoundDef`, `soundDefs`), and tuned by knobs (`tuneKits`: loudness per kit, variety). Since 0.2.0
// (IMP-0022, narrowed) a game overrides one cue's fields by id (`overrideCues`) and loads only the cues it plays
// (`pickCues`); `sfxIssues`' `plays` checks such a partial load.
import { checkRecipe, peakOf, renderSound, type SoundDef, type SoundRecipe } from "@rpg/audio";
import table from "./content/tables/sfx.json";
import schema from "./content/schemas/sfx.schema.json";
import strings from "./strings.json";

/** This unit's version (the manifest's and package.json's). */
export const SFX_KITS_VERSION = "0.2.0" as const;

/** The kits, and what each is for. */
export const SFX_KITS = {
  ui: "menus and screens: a press, a move between items, a back",
  footsteps: "a foot's plant, by the surface under it",
  pickups: "picking things up, gathering, a level-up chime",
  combat: "hits by kind, a defeat, swings by weapon, a dodge, a block",
  creatures: "an enemy noticing the player",
  props: "breakable things breaking",
} as const;

/** A kit's name. */
export type SfxKit = keyof typeof SFX_KITS;

/** The surfaces the footsteps kit covers (`step.<surface>`). */
export const FOOTSTEP_SURFACES = ["grass", "dirt", "stone", "sand", "water"] as const;

/** One cue, as its schema describes it (`src/content/schemas/sfx.schema.json`). */
export interface SfxPreset {
  id: string;
  kit: SfxKit;
  bus: SoundDef["bus"];
  /** Level at play (dB). */
  gain?: number;
  /** Sends a caption every time it plays. */
  important?: boolean;
  /** The caption's string (required when important). */
  caption?: { table: string; id: string };
  priority?: number;
  range?: number;
  /** Random pitch per play (± semitones) and level (± dB). */
  pitch?: number;
  vol?: number;
  recipe: SoundRecipe;
  reason: string;
}

/** The cues as a content table (`{ table: "sfx", version, rows }`). */
export const SFX_KITS_TABLE = table as unknown as { table: "sfx"; version: number; rows: SfxPreset[] };

/** The table's row schema (JSON Schema, with @rpg/content's `x-ref` for the caption). */
export const SFX_KITS_SCHEMA = schema as Record<string, unknown>;

/** The content this unit adds to a game, in @rpg/content's shape. */
export const SFX_KIT_CONTENT = {
  package: "@rpg-unit/sfx-kits",
  version: SFX_KITS_VERSION,
  tables: [SFX_KITS_TABLE],
  schemas: { sfx: SFX_KITS_SCHEMA },
} as const;

/** English defaults for the captions (`{ id, text }` rows for a strings table). */
export const SFX_KIT_STRINGS = strings as { id: string; text: string }[];

/** The cues of one kit, in table order. */
export function kitCues(kit: SfxKit, rows: readonly SfxPreset[] = SFX_KITS_TABLE.rows): SfxPreset[] {
  return rows.filter((r) => r.kit === kit);
}

/** A cue as an @rpg/audio `SoundDef` (only the fields the engine reads). `caption` overrides the caption's string id
 *  (null: none, e.g. a planted defect); by default it is the row's. */
export function toSoundDef(r: SfxPreset, o: { caption?: string | null } = {}): SoundDef {
  return {
    recipe: r.recipe,
    bus: r.bus,
    ...(r.gain !== undefined ? { gain: r.gain } : {}),
    ...(r.important ? { important: true } : {}),
    caption: o.caption !== undefined ? o.caption : (r.caption?.id ?? null),
    ...(r.priority !== undefined ? { priority: r.priority } : {}),
    ...(r.range !== undefined ? { range: r.range } : {}),
    ...(r.pitch !== undefined ? { pitch: r.pitch } : {}),
    ...(r.vol !== undefined ? { vol: r.vol } : {}),
  };
}

/** Every cue (of the given kits, or all) as `SoundDef`s by id, in table order. */
export function soundDefs(rows: readonly SfxPreset[] = SFX_KITS_TABLE.rows, kits?: readonly SfxKit[]): Record<string, SoundDef> {
  const out: Record<string, SoundDef> = {};
  for (const r of rows) if (!kits || kits.includes(r.kit)) out[r.id] = toSoundDef(r);
  return out;
}

/** The perceptual knobs (rule 13). */
export const SFX_KIT_KNOBS = {
  loudness: "louder or quieter, kit by kit: dB added to every cue's level in a kit (`{ ui: -3, footsteps: 2 }`)",
  variety: "more or less varied: scales each cue's random pitch and level per play (0 plays every cue the same; 1 as tuned)",
} as const;

/** Knob values. */
export interface SfxKnobs {
  loudness?: Partial<Record<SfxKit, number>>;
  variety?: number;
}

const round = (x: number): number => Math.round(x * 1e6) / 1e6;

/** The cues turned by the knobs (new rows; the input is untouched). With no knobs, equal to the input. */
export function tuneKits(rows: readonly SfxPreset[], knobs: SfxKnobs = {}): SfxPreset[] {
  const variety = knobs.variety ?? 1;
  if (!(variety >= 0)) throw new Error(`sfx kits: variety must be ≥ 0 (got ${variety})`);
  return rows.map((r) => {
    const out = structuredClone(r);
    const db = knobs.loudness?.[r.kit] ?? 0;
    if (db) out.gain = round((out.gain ?? 0) + db);
    if (variety !== 1) {
      if (out.pitch !== undefined) out.pitch = round(out.pitch * variety);
      if (out.vol !== undefined) out.vol = round(out.vol * variety);
    }
    return out;
  });
}

/** A game's change to one kit cue (0.2.0, IMP-0022): any of its fields but its id and kit, with why. `recipe` replaces
 *  the whole recipe. The cue keeps its id, so the game's mix and events still name it. Note the engine seeds a cue's
 *  variants from its id: a recipe moved here from a game cue of another id renders other noise. */
export type SfxOverride = Partial<Omit<SfxPreset, "id" | "kit" | "reason">> & { reason: string };

/** The cues with a game's overrides applied, by cue id (new rows, in table order; the input is untouched). Throws on
 *  an id the rows lack, so a renamed kit cue can't leave an override silently unused. */
export function overrideCues(rows: readonly SfxPreset[], overrides: Readonly<Record<string, SfxOverride>>): SfxPreset[] {
  for (const id of Object.keys(overrides)) if (!rows.some((r) => r.id === id)) throw new Error(`sfx kits: no cue "${id}" to override`);
  return rows.map((r) => {
    const o = overrides[r.id];
    if (!o) return structuredClone(r);
    const { reason, ...fields } = o;
    return { ...structuredClone(r), ...structuredClone(fields), id: r.id, kit: r.kit, reason: `${r.reason} Overridden by the game: ${reason}` };
  });
}

/** Only the cues a game plays (0.2.0, IMP-0022), so its warm-up renders nothing it never plays: every cue of `kits`,
 *  and the cues named in `cues`, in table order. Throws on a cue id the rows lack. With neither, nothing. */
export function pickCues(rows: readonly SfxPreset[], pick: { kits?: readonly SfxKit[]; cues?: readonly string[] }): SfxPreset[] {
  const ids = new Set(pick.cues ?? []);
  for (const id of ids) if (!rows.some((r) => r.id === id)) throw new Error(`sfx kits: no cue "${id}" to load`);
  // the planted defect sits here, beside what it breaks
  if (SFX_KIT_FAULTS.pickDropsNamed) ids.clear();
  return rows.filter((r) => pick.kits?.includes(r.kit) || ids.has(r.id));
}

/** Planted defects the unit owns, beside what they break. */
export const SFX_KIT_FAULTS = {
  /** `pickCues` loads only whole kits: the cues named one by one are left out (the "played but not loaded" check goes
   *  red on a game's partial load). */
  pickDropsNamed: false,
};

/** The bounds the checks hold a cue to (the procedural-audio rules): a rendered peak at most 0.89, over 0.01 (not
 *  silent), and its first and last samples under 0.001 (no click) unless it loops. */
export const SFX_BOUNDS = { peak: 0.8901, silent: 0.01, click: 1e-3 } as const;

/** What's wrong with a set of cues (empty: sound). Without rendering: unique ids, the recipe language's check, an
 *  important cue names a caption (and, given `strings`, one that exists), every kit has a cue, the footsteps cover
 *  every surface. With `render`: each variant renders finite, audible, under the peak, with no click at its ends.
 *  Given `plays` (0.2.0: a partial load, the ids of the cues the game plays from these rows), the coverage checks are
 *  that load's instead: every cue it plays is here, whatever kits and surfaces it left out. */
export function sfxIssues(rows: readonly SfxPreset[], o: { render?: boolean; strings?: ReadonlySet<string>; plays?: readonly string[] } = {}): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const r of rows) {
    if (seen.has(r.id)) out.push(`${r.id}: duplicate id`);
    seen.add(r.id);
    for (const m of checkRecipe(r.recipe)) out.push(`${r.id}: ${m}`);
    if (r.important && !r.caption) out.push(`${r.id}: an important cue with no caption`);
    if (r.caption && o.strings && !o.strings.has(r.caption.id)) out.push(`${r.id}: its caption ${r.caption.id} has no string`);
    if (!o.render) continue;
    for (let v = 0; v < Math.max(1, r.recipe.vars ?? 1); v++) {
      const x = renderSound(r.id, r.recipe, v);
      const c = x.channels[0]!;
      if (!c.every(Number.isFinite)) {
        out.push(`${r.id}/${v}: non-finite samples`);
        continue;
      }
      const p = peakOf(x.channels);
      if (!(p > SFX_BOUNDS.silent)) out.push(`${r.id}/${v}: silent (peak ${p.toFixed(4)})`);
      if (p > SFX_BOUNDS.peak) out.push(`${r.id}/${v}: peak ${p.toFixed(3)} over ${SFX_BOUNDS.peak}`);
      if (!r.recipe.loop && (Math.abs(c[0]!) >= SFX_BOUNDS.click || Math.abs(c.at(-1)!) >= SFX_BOUNDS.click)) out.push(`${r.id}/${v}: clicks at its ends`);
    }
  }
  if (o.plays) {
    for (const id of new Set(o.plays)) if (!seen.has(id)) out.push(`${id}: played but not loaded`);
    return out;
  }
  for (const k of Object.keys(SFX_KITS) as SfxKit[]) if (!rows.some((r) => r.kit === k)) out.push(`kit ${k}: no cues`);
  for (const s of FOOTSTEP_SURFACES) if (!rows.some((r) => r.id === `step.${s}`)) out.push(`footsteps: no cue for ${s} (step.${s})`);
  return out;
}
